import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X, Phone, MoveUpRight } from "lucide-react";
import { Button } from "components/ui/button";
import { Separator } from "components/ui/separator";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "components/ui/collapsible";
import { cn } from "lib/utils";
import { ReactComponent as Logo } from "../../assets/owners-first-logo.svg";
import { contact, navigation } from "../content";

export function Brand() {
  return (
    <Link to="/" className="site-brand" aria-label="Owners First home">
      <Logo aria-hidden="true" />
    </Link>
  );
}
export function RequestButton({
  variant = "default",
  size = "lg",
  children = "Request management",
}) {
  return (
    <Button variant={variant} size={size} asChild>
      <Link to="/proposal">
        {children}
        <ArrowUpRight data-icon="inline-end" aria-hidden="true" />
      </Link>
    </Button>
  );
}
export function ArrowLink({ to, children, variant = "outline" }) {
  return (
    <Button variant={variant} size="lg" asChild>
      <Link to={to}>
        {children}
        <ArrowUpRight data-icon="inline-end" aria-hidden="true" />
      </Link>
    </Button>
  );
}

function Header() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [pathname]);
  return (
    <header
      className={cn(
        "site-header",
        pathname === "/" && "site-header-home",
        open && "site-header-open",
      )}
    >
      <Collapsible open={open} onOpenChange={setOpen} className="site-wrap">
        <div className="site-nav">
          <Brand />
          <nav aria-label="Main navigation" className="site-nav-links">
            {navigation.map((item) => (
              <NavLink to={item.path} key={item.path}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="site-nav-actions">
            <span className="site-desktop-request">
              <RequestButton size="default" />
            </span>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="site-menu-button"
                aria-label={open ? "Close menu" : "Open menu"}
              >
                {open ? <X /> : <Menu />}
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>
        <CollapsibleContent className="site-mobile-menu">
          <nav aria-label="Mobile navigation">
            {navigation.map((item) => (
              <NavLink
                to={item.path}
                key={item.path}
                onClick={() => setOpen(false)}
              >
                {item.label}
                <ArrowUpRight aria-hidden="true" />
              </NavLink>
            ))}
            <RequestButton />
            <a href={contact.telephone} className="site-mobile-phone">
              <Phone aria-hidden="true" />
              {contact.phone}
            </a>
          </nav>
        </CollapsibleContent>
      </Collapsible>
    </header>
  );
}
export function ContactCTA() {
  return (
    <section className="site-contact-cta">
      <div className="site-wrap">
        <h2>
          Let’s put your
          <br />
          community first.
        </h2>
        <div>
          <p>
            Tell us about your property.
            <br />
            We’ll take it from there.
          </p>
          <RequestButton variant="secondary" />
        </div>
      </div>
    </section>
  );
}
function Footer() {
  return (
    <footer className="site-footer">
      <div className="site-wrap">
        <div className="site-footer-top">
          <Brand />
          <div className="site-footer-links">
            {navigation.map((item) => (
              <Link key={item.path} to={item.path}>
                {item.label}
              </Link>
            ))}
            <Link to="/proposal">Request management</Link>
          </div>
          <div className="site-footer-contact">
            <a href={`mailto:${contact.email}`}>
              {contact.email}
              <MoveUpRight aria-hidden="true" />
            </a>
            <a href={contact.telephone}>{contact.phone}</a>
          </div>
          <div className="site-emergency">
            <span>After-hours property emergencies</span>
            <a href={contact.emergencyTelephone}>
              {contact.emergency}
              <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </div>
        <Separator />
        <div className="site-footer-bottom">
          <p>© {new Date().getFullYear()} Owners First Body Corporate.</p>
          <span>People. Property. Community.</span>
          <Link to="/privacy">Privacy policy</Link>
        </div>
      </div>
    </footer>
  );
}
export default function SiteShell() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      let anchorId = hash.slice(1);
      try {
        anchorId = decodeURIComponent(anchorId);
      } catch {
        // Treat malformed URL escapes as a literal anchor.
      }
      const target = document.getElementById(anchorId);
      const focusTarget =
        target ||
        document.querySelector("#main-content h1") ||
        document.getElementById("main-content");
      if (focusTarget && !focusTarget.matches("button, a, input, [tabindex]"))
        focusTarget.setAttribute("tabindex", "-1");
      focusTarget?.focus({ preventScroll: true });
      if (target) target.scrollIntoView({ behavior: "instant" });
      else window.scrollTo({ top: 0, behavior: "instant" });
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);
  return (
    <div className="site">
      <a className="site-skip" href="#main-content">
        Skip to content
      </a>
      <Header />
      <main id="main-content" tabIndex={-1}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
